import { components } from "@appretio/api";
import type {
  CustomCellRendererProps,
  CustomTooltipProps
} from "ag-grid-react";
import {
  getModelLabel,
  isAlgorithmicProducer,
  modelOfProducer
} from "../../../../utils/algorithmicProducer";
import * as styles from "./ProducerCell.css";
import { formatProducer } from "./producerLabel";

type Row = Record<string, unknown>;
type HoldoutUnscoredReason = components["schemas"]["HoldoutUnscoredReason"];

const UNSCORED_COPY: Record<HoldoutUnscoredReason, string> = {
  short_train_range:
    "the training range is too short to hold out a scoring window",
  zero_holdout: "the last weeks of history are all zero",
  fit_failed: "the model failed to fit on the shortened range"
};

function asNumber(value: unknown): number | null {
  return typeof value === "number" ? value : null;
}

function formatWape(holdoutWape: unknown): string | null {
  const wape = asNumber(holdoutWape);
  return wape === null ? null : `${(wape * 100).toFixed(1)}%`;
}

function isUnscoredReason(value: unknown): value is HoldoutUnscoredReason {
  return typeof value === "string" && value in UNSCORED_COPY;
}

/**
 * Producer cell for an algorithmic row: the label plus the confidence badge
 * `142/156 wks · 8.4% @13w` (SPEC § Result rows). Other rows keep the
 * column's value formatter.
 */
export function ProducerCell({
  value,
  data
}: CustomCellRendererProps<Row, string>) {
  const label = formatProducer(value ?? "");
  const available = asNumber(data?.weeks_available);
  if (!isAlgorithmicProducer(value) || available === null) return <>{label}</>;
  const nonzero = asNumber(data?.weeks_nonzero);
  const wape = formatWape(data?.holdout_wape);
  const horizon = asNumber(data?.holdout_weeks);
  const weeks = nonzero === null ? `${available}` : `${nonzero}/${available}`;
  const score = wape
    ? ` · ${wape}${horizon === null ? "" : ` @${horizon}w`}`
    : "";
  const description =
    (nonzero === null
      ? `${available} weeks of history`
      : `${nonzero} of ${available} weeks of history nonzero`) +
    (wape
      ? `, backtest WAPE ${wape}${horizon === null ? "" : ` at a ${horizon}-week horizon`}`
      : "");
  return (
    <span className={styles.cell}>
      <span>{label}</span>
      <span className={styles.badge} role="img" aria-label={description}>
        {weeks} wks{score}
      </span>
    </span>
  );
}

/**
 * Load-metadata tooltip for an algorithmic row: model and ground truth, the
 * fitted history with its counts and calendar-named span, and the backtest
 * score with the window it describes — or why there is none. The fields are
 * denormalized onto the row by `useAlgorithmicForecastState`.
 */
export function ProducerTooltip({
  value,
  data
}: CustomTooltipProps<Row, string>) {
  if (!isAlgorithmicProducer(value)) return null;
  const available = asNumber(data?.weeks_available);
  const nonzero = asNumber(data?.weeks_nonzero);
  const wape = formatWape(data?.holdout_wape);
  const holdoutWeeks = asNumber(data?.holdout_weeks);
  const unscored = data?.holdout_unscored;
  const range = data?.algo_training_range;
  return (
    <div className={styles.tooltip} role="tooltip">
      <div>
        Model <strong>{getModelLabel(modelOfProducer(value))}</strong> · Ground
        truth <strong>{String(data?.algo_ground_truth ?? "Actuals")}</strong>
      </div>
      <div>
        {available === null ? (
          "History diagnostics unavailable"
        ) : (
          <>
            <strong>{available} weeks</strong> of history
            {nonzero === null ? "" : ` (${nonzero} nonzero`}
            {typeof range === "string" ? `, ${range}` : ""}
            {nonzero === null ? "" : ")"}
          </>
        )}
      </div>
      <div>
        {wape ? (
          <>
            Backtest WAPE <strong>{wape}</strong>
            {holdoutWeeks === null
              ? ""
              : ` (${holdoutWeeks}-week horizon, last ${holdoutWeeks} weeks of history)`}
          </>
        ) : (
          `Backtest WAPE unavailable${isUnscoredReason(unscored) ? `: ${UNSCORED_COPY[unscored]}` : ""}`
        )}
      </div>
    </div>
  );
}
